import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { AntDataResponse } from '@antony/ng-ui';
import { AntCollectionSortLoadingParams, ResourceCollectionApiService } from '@api/models';
import { ContactPersonCustomersApiService, CustomerDto } from '@api/api.service';
import { LoadingParamsUtils } from '@api/utils';
import { concatMap, map, switchMap, toArray } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ContactPersonCustomerService implements ResourceCollectionApiService<CustomerDto> {
  constructor(private api: ContactPersonCustomersApiService) {}

  getData(params: AntCollectionSortLoadingParams<CustomerDto>): Observable<AntDataResponse<CustomerDto>> {
    return this.api
      .getPagedCustomers(
        LoadingParamsUtils.getIdFrom(params),
        LoadingParamsUtils.getSkipFrom(params),
        LoadingParamsUtils.getTakeFrom(params),
        LoadingParamsUtils.getFilterNamesFrom(params),
        LoadingParamsUtils.getFilterValueFrom(params),
        LoadingParamsUtils.getSortNamesFrom(params),
        LoadingParamsUtils.getApiSortDirection(params)
      )
      .pipe(
        map((res) => ({
          data: res.data || [],
          maxCount: res.maxCount || 0,
        }))
      );
  }

  getIndirectCustomers(params: AntCollectionSortLoadingParams<CustomerDto>): Observable<AntDataResponse<CustomerDto>> {
    return this.api
      .getPagedIndirectCustomers(
        LoadingParamsUtils.getIdFrom(params),
        LoadingParamsUtils.getSkipFrom(params),
        LoadingParamsUtils.getTakeFrom(params),
        LoadingParamsUtils.getFilterNamesFrom(params),
        LoadingParamsUtils.getFilterValueFrom(params),
        LoadingParamsUtils.getSortNamesFrom(params),
        LoadingParamsUtils.getApiSortDirection(params)
      )
      .pipe(
        map((res) => ({
          data: res.data || [],
          maxCount: res.maxCount || 0,
        }))
      );
  }

  add(contactPersonId: number, customerId: number): Observable<boolean> {
    return this.api.createCustomer(contactPersonId, customerId).pipe(map((res) => res.success));
  }

  addBulk(contactPersonId: number, customerIds: number[]): Observable<boolean[]> {
    return from(customerIds).pipe(
      concatMap((customerId) => this.add(contactPersonId, customerId)),
      toArray()
    );
  }

  remove(contactPersonId: number, customerId: number): Observable<boolean> {
    return this.api.deleteCustomer(contactPersonId, customerId).pipe(map((res) => res.success));
  }

  removeBulk(contactPersonId: number, customerIds: number[]): Observable<boolean[]> {
    return this.api.deleteBulkCustomers(contactPersonId, customerIds).pipe(
      map((res) => res.data || []),
      switchMap((responses) => from(responses)),
      map((res) => res.success),
      toArray()
    );
  }
}
